import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Clock, Music, Users, Sparkles } from "lucide-react";
import djNight from "@/assets/dj-night.jpg";

interface Event {
  id: string;
  title: string;
  description: string | null;
  day_of_week: string | null;
  start_time: string | null;
  end_time: string | null;
  event_type: string;
}

const eventIcons: Record<string, typeof Clock> = {
  happy_hour: Clock,
  ladies_night: Users,
  dj_night: Music,
  live_music: Sparkles,
  recurring: Clock,
  special: Sparkles,
};

const formatTime = (time: string | null) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}${minutes !== '00' ? ':' + minutes : ''}${ampm}`;
};

const formatSchedule = (event: Event) => {
  let schedule = '';
  if (event.day_of_week) {
    if (event.day_of_week === 'daily') {
      schedule = 'Daily';
    } else if (event.day_of_week.includes(',')) {
      schedule = event.day_of_week.split(',').map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(' & ');
    } else {
      schedule = 'Every ' + event.day_of_week.charAt(0).toUpperCase() + event.day_of_week.slice(1);
    }
  }
  if (event.start_time) {
    schedule += ` ${formatTime(event.start_time)}`;
    if (event.end_time) {
      schedule += ` - ${formatTime(event.end_time)}`;
    }
  }
  return schedule;
};

export function EventsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');

    if (!error && data) {
      setEvents(data);
    }
    setLoading(false);
  };

  return (
    <section id="events" className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={djNight} alt="DJ Night at Tommy Hour" className="w-full h-full object-cover opacity-20" loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
      </div>

      <div className="container mx-auto px-4 relative" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-primary font-medium tracking-[0.2em] text-sm mb-4">WHAT'S ON</p>
          <h2 className="section-heading text-foreground underline-gold">EVENTS</h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {loading ? (
            [1, 2, 3, 4].map((i) => (
              <div key={i} className="h-48 bg-card/50 animate-pulse rounded-lg" />
            ))
          ) : (
            events.map((event, index) => {
              const IconComponent = eventIcons[event.event_type] || Clock;
              return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative"
                >
                  <div className="glass p-8 rounded-lg h-full transition-all duration-300 group-hover:shadow-[0_0_30px_hsla(43,74%,49%,0.2)] group-hover:-translate-y-2">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                      <IconComponent className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-display text-2xl text-foreground mb-2">{event.title}</h3>
                    <p className="text-primary text-sm font-semibold mb-3">{formatSchedule(event)}</p>
                    <p className="text-muted-foreground text-sm">{event.description}</p>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-b-lg" />
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 text-center"
        >
          <div className="inline-block glass px-8 py-6 rounded-lg">
            <p className="text-foreground/70 mb-2">Want to host a private event?</p>
            <a href="#contact" className="text-primary font-semibold hover:underline underline-offset-4">
              Contact us for bookings →
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}